import * as React from "react";
import { Accordion } from "../../../../../../../react-core/Html/Accordion";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { bankAccountParseWithHolderName, boolParse, dateParser, prepareTypography } from "../ReviewParser";
import { FrequencyArraysMap, quoteSippIncomePaymentDetailsLabels } from "../../../NewQuoteWizard/Resource/Content";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { CappedAndFlexiTableReview, ColumnType } from "./CappedAndFlexiTableReview";
import { PensionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { ContributionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/ContributionBase";
import { ApplyModel } from "../../../../../../../Wrap/Models/Models/Processes/Apply/ApplyModel";
import { PensionArrangementType } from "../../../../../../../Wrap/Constants/Enums/Domain/PensionArrangementType";
import { nbSippIncomePaymentDetailsLabels } from "../../../NewBusinessWizard/Resource/Contents";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { CappedModel } from "../../../../../../../Wrap/Models/Domains/Drawdown/CappedModel";
import { FlexiModel } from "../../../../../../../Wrap/Models/Domains/Drawdown/FlexiModel";
import {
    calculateMaximumAvailablePerPayment,
    calculateMaximumGrossOneOffIncomePaymentWithStandardAlgorithm,
    calculateMaximumIncomePerPaymentWithStandardAlgorithm,
    calculateMaximumIncomePerPaymentWithTargetAlgorithm,
    calculateMaximumInitialIncomePayment,
} from "../../SIPP/IncomePaymentDetails/Helper/IncomePaymentHelper";

interface IIncomePaymentReviewProps {
    data: PensionBase;
    process: ProcessType;
    colNum: number;
    onEditAction: () => void;
    model: ContributionBase | ApplyModel;
    isOpen?: boolean;
    isITFC: boolean;
}

export const IncomePaymentReview = (props: IIncomePaymentReviewProps) => {
    const { data, process, isITFC } = props;
    const columnsTypes = {
        CappedcolumnsType: [
            { Title: "From", Key: "From", type: ColumnType.PlainText },
            { Title: "Amount", Key: "Amount", type: ColumnType.Money },
            { Title: "Type", Key: "Type", type: ColumnType.PlainText },
            { Title: "Taxable", Key: "Taxable", type: ColumnType.bool },
            { Title: "GAD Maximum", Key: "GADMaximum", type: ColumnType.Money },
            { Title: "Income taken to date", Key: "Incometakentodate", type: ColumnType.Money },
            { Title: "Last mandatory review", Key: "Lastmandatoryreview", type: ColumnType.PlainText },
            {
                Title: "Maximum Income Payment For Remainder Of Pension Year",
                Key: "MaximumIncomePaymentForRemainderOfPensionYear",
                type: ColumnType.Money,
            },
            {
                Title: "MaximumI ncome Payment For Future Pension Years",
                Key: "MaximumIncomePaymentForFuturePensionYears",
                type: ColumnType.Money,
            },
        ],
        FlexicolumnsType: [
            { Title: "From", Key: "From", type: ColumnType.PlainText },
            { Title: "Amount", Key: "Amount", type: ColumnType.Money },
            { Title: "Type", Key: "Type", type: ColumnType.PlainText },
            { Title: "Maximum Income Per Payment", Key: "MaximumIncomePerPayment", type: ColumnType.Money },
        ],
    };

    const incomePaymentConfig = () => {
        if (!data) {return [];}
        const income =
            data?.DrawdownModel?.Income ||
            data?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome;
        const incomePaymentConfig = [
            {
                title: quoteSippIncomePaymentDetailsLabels.targetSpecificArrangements,
                data: boolParse(income?.TargetSpecificArrangements),
            },
        ];
        if (income?.TargetSpecificArrangements == false) {
            if (
                data?.DrawdownModel?.DrawdownType != DrawdownType.Capped &&
                !data?.PensionTransfers?.find((transfer) => transfer.DrawdownType === DrawdownType.Capped)
            ) {
                incomePaymentConfig.push({
                    title: quoteSippIncomePaymentDetailsLabels.takeDifferentInitialIncomePayment,
                    data: boolParse(income?.TakeDifferentInitialIncomePayment),
                });
                if (income?.TakeDifferentInitialIncomePayment) {
                    incomePaymentConfig.push(
                        {
                            title: quoteSippIncomePaymentDetailsLabels.maximumInitialIncomePayment,
                            data: MoneyDisplayHelper.getMoneyFormat({
                                Amount: calculateMaximumInitialIncomePayment(process, data, isITFC),
                            }),
                        },
                        {
                            title: quoteSippIncomePaymentDetailsLabels.grossInitialIncomePayment,
                            data: MoneyDisplayHelper.getMoneyFormat(income?.GrossInitialIncomePayment),
                        }
                    );
                }
            }
        }

        if (income?.TargetSpecificArrangements == true) {
            incomePaymentConfig.push({
                title: quoteSippIncomePaymentDetailsLabels.maximumIncomePerPayment,
                data: MoneyDisplayHelper.getMoneyFormat({
                    Amount: calculateMaximumIncomePerPaymentWithTargetAlgorithm(process, data, isITFC),
                }),
            });
            incomePaymentConfig.push({
                title: quoteSippIncomePaymentDetailsLabels.grossIncomePerPaymentFromPension,
                data: MoneyDisplayHelper.getMoneyFormat(income?.GrossIncomePerPaymentFromPension),
            });
        } else {
            incomePaymentConfig.push({
                title: quoteSippIncomePaymentDetailsLabels.maximumIncomePerPayment,
                data: MoneyDisplayHelper.getMoneyFormat({
                    Amount: calculateMaximumIncomePerPaymentWithStandardAlgorithm(process, data, isITFC),
                }),
            });
            incomePaymentConfig.push({
                title: quoteSippIncomePaymentDetailsLabels.grossIncomePerPayment,
                data: MoneyDisplayHelper.getMoneyFormat(income?.GrossIncomePerPayment),
            });
        }

        incomePaymentConfig.push(
            {
                title: quoteSippIncomePaymentDetailsLabels.incomeFrequency,
                data: FrequencyArraysMap[income?.Frequency],
            },
            {
                title: quoteSippIncomePaymentDetailsLabels.firstPaymentDate,
                data: income?.NextPayment ? dateParser(income?.NextPayment) : null,
            },
            {
                title: "End date",
                data:
                    income?.PaymentEnd === null
                        ? nbSippIncomePaymentDetailsLabels.untilNotified
                        : dateParser(income?.PaymentEnd),
            }
        );

        return incomePaymentConfig;
    };

    const oneoffPaymentConfig = () => {
        if (!data) {return [];}
        const oneOff =
            data?.DrawdownModel?.OneOffTaxableWithdrawalDetails ||
            data?.PensionTransfers[0]?.OneOffTaxableWithdrawalDetails;
        const oneOffPaymentConfig = [];
        if (oneOff != null && oneOff?.Amount?.Amount > 0) {
            oneOffPaymentConfig.push({
                title: <strong>One-off income payment</strong>,
                data: " ",
            });
            // TODO - need split target & standard algorithm for maximum income payment
            oneOffPaymentConfig.push({
                title: quoteSippIncomePaymentDetailsLabels.maximumGrossOneOffIncomePayment,
                data: MoneyDisplayHelper.getMoneyFormat({
                    Amount: calculateMaximumGrossOneOffIncomePaymentWithStandardAlgorithm(process, data, isITFC),
                }),
            });
            oneOffPaymentConfig.push({
                title: quoteSippIncomePaymentDetailsLabels.oneOffIncomePayment,
                data: MoneyDisplayHelper.getMoneyFormat(oneOff.Amount),
            });
        }
        return oneOffPaymentConfig;
    };

    const regularIncomePaymentConfig = () => {
        if (!data) {return [];}
        const income =
            data?.DrawdownModel?.Income ||
            data?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome;
        const regularIncomePayment = [
            {
                title: quoteSippIncomePaymentDetailsLabels.taxableIncomeRequiredInAddition,
                data: boolParse(income?.TaxableIncomeRequiredInAddition),
            },
        ];
        if (income?.TaxableIncomeRequiredInAddition) {
            regularIncomePayment.push(
                {
                    title: quoteSippIncomePaymentDetailsLabels.maximumAvailablePerPayment,
                    data: MoneyDisplayHelper.getMoneyFormat({
                        Amount: calculateMaximumAvailablePerPayment(process, data?.DrawdownModel),
                    }),
                },
                {
                    title: quoteSippIncomePaymentDetailsLabels.grossIncomePerPayment,
                    data: MoneyDisplayHelper.getMoneyFormat(income?.GrossIncomePerPayment),
                },
                {
                    title: quoteSippIncomePaymentDetailsLabels.incomeFrequency,
                    data: FrequencyArraysMap[data?.DrawdownModel?.RegularCrystallisationDetails?.Frequency],
                },
                {
                    title: quoteSippIncomePaymentDetailsLabels.firstPaymentDate,
                    data: dateParser(data?.DrawdownModel?.RegularCrystallisationDetails?.FirstPaymentDay),
                },
                {
                    title: quoteSippIncomePaymentDetailsLabels.endDate,
                    data: income?.PaymentEnd ? dateParser(income?.PaymentEnd) : null,
                }
            );
        }
        return regularIncomePayment;
    };

    const assetsToSellConfig = () => {
        if (!data) {return [];}
        const DrawdownModel = data?.DrawdownModel;
        const assetsToSell = [];
        assetsToSell.push({
            title: <strong>Choose assets to sell</strong>,
            data: " ",
        });
        assetsToSell.push({
            title: "Choose asset selection method",
            data: DrawdownModel?.IncomeDisinvestmentAllocations?.length
                ? "From specified assets"
                : "Proportionally across all assets",
        });

        if (DrawdownModel?.IncomeDisinvestmentAllocations?.length) {
            for (const el of DrawdownModel?.IncomeDisinvestmentAllocations) {
                const name = data?.PreRetirementInstrumentSelections?.Instruments?.find((selection) => {
                    return selection?.Instrument.ProductId === el.ProductId;
                }).Instrument.Name;
                assetsToSell.push({
                    title: <div className="pl-10">{name}</div>,
                    data: <div className="pl-10">{el?.Percentage}%</div>,
                });
            }
        }
        return assetsToSell;
    };

    const bankAccountDetailsConfig = () => {
        if (!data) {return [];}
        const IncomePaymentAllocations = data?.DrawdownModel?.Income?.IncomePaymentAllocations;
        if (!IncomePaymentAllocations) {return [];}
        const bankDetails = [];
        bankDetails.push({
            title: <strong>Bank account details</strong>,
            data: " ",
        });
        const backAccounts = [...props.model.AllBankAccounts, ...props.model.BankAccountInstructions];
        IncomePaymentAllocations.map((bankAccount) => {
            bankDetails.push({
                title: "Bank account",
                data: bankAccountParseWithHolderName(bankAccount.BankAccountId, backAccounts, bankAccount.Destination),
            });

            bankDetails.push({
                title: "Portion of PCLS",
                data: bankAccount.PclsAllocation + "%",
            });
            bankDetails.push({
                title: "Proportion of income",
                data: bankAccount.Allocation + "%",
            });
        });
        return bankDetails;
    };

    const getCappedModel = (): CappedModel[] => {
        const existingCappedModel = data?.DrawdownModel?.ExistingArrangements?.CappedModels || [];
        const drawdownCappedModel: CappedModel[] =
            data?.DrawdownModel?.DrawdownType == DrawdownType.Capped
                ? [
                      {
                          Type: "Member",
                          From: nbSippIncomePaymentDetailsLabels.crystallisation,
                          Amount: data?.DrawdownModel?.CrystallisationDetails?.CrystallisedValue?.Amount,
                          Taxable: null,
                          GadMaximum: data?.DrawdownModel?.GadRates?.GadMaximum,
                          IncomeTakenToDate: data?.DrawdownModel?.Income?.CurrentYearIncome?.Amount,
                          LastMandatoryReview: null,
                          MaximumIncomePaymentForRemainderOfPensionYear: 20000,
                          MaximumIncomePaymentForFuturePensionYears: 50000,
                      },
                  ]
                : [];
        const pensionTransferCapped: CappedModel[] = (data?.PensionTransfers || [])
            .filter(
                (ele) =>
                    ele.DrawdownType == DrawdownType.Capped && ele.PensionSource == PensionArrangementType.Crystallised
            )
            .map((ele) => {
                return {
                    Type: ele?.IsInheritedFund ? "Inherited" : "Member",
                    From: ele?.ProviderDetails?.Name,
                    Amount: ele?.CurrentValue?.Amount,
                    Taxable: null,
                    GadMaximum: ele?.IncomePaymentDetails?.GadMaximum?.Amount,
                    IncomeTakenToDate: ele?.IncomePaymentDetails?.CurrentYearIncome?.Amount,
                    LastMandatoryReview: ele?.IncomePaymentDetails?.LastGadReviewDate,
                    MaximumIncomePaymentForRemainderOfPensionYear: 20000,
                    MaximumIncomePaymentForFuturePensionYears: 50000,
                };
            });
        return drawdownCappedModel.concat(pensionTransferCapped, existingCappedModel);
    };

    const getFlexiModel = (): FlexiModel[] => {
        const existingCappedModel = data?.DrawdownModel?.ExistingArrangements?.FlexiModels || [];
        const drawdownCappedModel: FlexiModel[] =
            data?.DrawdownModel?.DrawdownType == DrawdownType.Flexible
                ? [
                      {
                          Type: "Member",
                          From: nbSippIncomePaymentDetailsLabels.crystallisation,
                          Amount: data?.DrawdownModel?.CrystallisationDetails?.CrystallisedValue?.Amount,
                          MaximumIncomePerPayment: 2000,
                      },
                  ]
                : [];
        const pensionTransferCapped = (data?.PensionTransfers || [])
            .filter(
                (ele) =>
                    ele.DrawdownType == DrawdownType.Flexible &&
                    ele.PensionSource == PensionArrangementType.Crystallised
            )
            .map((ele) => {
                return {
                    Type: ele?.IsInheritedFund ? "Inherited" : "Member",
                    From: ele?.ProviderDetails?.Name,
                    Amount: ele?.CurrentValue?.Amount,
                    MaximumIncomePerPayment: 2000,
                };
            });
        return drawdownCappedModel.concat(pensionTransferCapped, existingCappedModel);
    };

    return (
        <Accordion label="Income payment details" isOpen={false} onEdit={props.onEditAction}>
            {data?.DrawdownModel?.DrawdownType == DrawdownType.Regular ? (
                <div className="pl-34">
                    <Typography {...prepareTypography(regularIncomePaymentConfig(), 1, "common")}></Typography>
                    <br />
                    <Typography {...prepareTypography(oneoffPaymentConfig(), 1, "common")}></Typography>
                </div>
            ) : (
                <div className="pl-34">
                    <Typography {...prepareTypography(incomePaymentConfig(), 1, "common")}></Typography>
                    <br />
                    <Typography {...prepareTypography(oneoffPaymentConfig(), 1, "common")}></Typography>
                </div>
            )}
            {data?.DrawdownModel?.Income?.TargetSpecificArrangements && (
                <div className="pl-34">
                    {getCappedModel().length > 0 && (
                        <CappedAndFlexiTableReview
                            tableTitle="Capped"
                            data={getCappedModel()}
                            columnsType={columnsTypes.CappedcolumnsType}
                        />
                    )}
                    {getFlexiModel().length > 0 && (
                        <CappedAndFlexiTableReview
                            tableTitle={"Flexi"}
                            data={getFlexiModel()}
                            columnsType={columnsTypes.FlexicolumnsType}
                        />
                    )}
                </div>
            )}

            {process != ProcessType.Quote && (
                <>
                    <div className="pl-34">
                        <Typography {...prepareTypography(assetsToSellConfig(), 1, "common")}></Typography>
                    </div>
                    <div className="pl-34">
                        <Typography {...prepareTypography(bankAccountDetailsConfig(), 1, "common")} />
                    </div>
                </>
            )}
        </Accordion>
    );
};
